import { Entity as CurriculumEntity } from '@mathflat/domain/@entities/Curriculum/dto'
import type { SchoolSystemRequestDropdownParams } from '@mathflat/domain/@entities/SchoolSystem/api'
import type {
  AllGradeSubject,
  Revision,
  SchoolType,
  Semester,
} from '@mathflat/domain/@entities/SchoolSystem/schema'
import type {
  DefaultApiParamT,
  DropdownParams,
  PageParamT,
} from '@mathflat/shared/@types/mathflatApi'
import { makeObservable, observable } from 'mobx'
import type { ReqParamData } from 'src/dto'
import { ReqParams } from 'src/dto/common/ReqParams'

import type { ExpandedGradeSemesterSubject } from '@/service/curriculumCore.service'
import { curriculumCoreService } from '@/service/curriculumCore.service'

export type CoreDropdownInfoT = ExpandedGradeSemesterSubject[]

export type DropdownValue_SchoolType = DropdownParams<SchoolType>
export type DropdownValue_Semester = DropdownParams<Semester>
export type DropdownValue_Revision = DropdownParams<Revision>
export type DropdownValue_Grade = DropdownParams<AllGradeSubject>

/** 드롭다운 필터의 'ALL'/null 을 API 미전송(undefined)으로 정규화한다. */
export const omitAll = <T>(value: DropdownParams<T> | null | undefined): T | undefined =>
  value == null || value === 'ALL' ? undefined : value

type DropdownPairList_SchoolGradeSemesterSubject = {
  elementaryDropdownList: CoreDropdownInfoT
  middleDropdownList: CoreDropdownInfoT
  highDropdownList: CoreDropdownInfoT
}

// ReqParams['params']에 추가될 값들
export type SchoolGradeSemesterSubjectParamT = DefaultApiParamT<SchoolSystemRequestDropdownParams>

// 이 클래스의 field들
export type SchoolGradeSemesterSubjectFilterData<T extends DefaultApiParamT> = ReqParamData<T> &
  Partial<DropdownPairList_SchoolGradeSemesterSubject> & {
    selectedTrieKey?: CurriculumEntity.TrieKey | 'ALL'
  }

export type FilterType = 'GradeSemester' | 'GradeSubject'

export class SchoolGradeSemesterSubjectFilter<
  ParamT extends SchoolGradeSemesterSubjectParamT,
> extends ReqParams<ParamT> {
  selectedTrieKey: CurriculumEntity.TrieKey | 'ALL' = 'ALL'

  elementaryDropdownList?: CoreDropdownInfoT
  middleDropdownList?: CoreDropdownInfoT
  highDropdownList?: CoreDropdownInfoT

  constructor(
    data?: Omit<
      SchoolGradeSemesterSubjectFilterData<ParamT>,
      keyof SchoolSystemRequestDropdownParams | 'selectedTrieKey'
    >,
  ) {
    const defaultParams: {
      page: PageParamT['page']
      size: PageParamT['size']
      isLast: PageParamT['isLast']
      schoolType: DropdownValue_SchoolType
      grade: DropdownValue_Grade
      semester: DropdownValue_Semester
      revision: DropdownValue_Revision
    } = {
      page: 0,
      size: 30,
      isLast: false,
      schoolType: 'ALL',
      grade: 'ALL',
      semester: 'ALL',
      revision: 'ALL',
    }

    const params = data?.params ? { ...defaultParams, ...data.params } : (defaultParams as ParamT)

    super({ params: params as ParamT })

    if (data?.elementaryDropdownList) this.elementaryDropdownList = data.elementaryDropdownList
    if (data?.middleDropdownList) this.middleDropdownList = data.middleDropdownList
    if (data?.highDropdownList) this.highDropdownList = data.highDropdownList

    const subscribedCurriculumInfo = [
      ...(this.elementaryDropdownList ?? []),
      ...(this.middleDropdownList ?? []),
      ...(this.highDropdownList ?? []),
    ].find(({ subscribeStatus }) => subscribeStatus === 'SUBSCRIBED')

    if (subscribedCurriculumInfo && !subscribedCurriculumInfo.isAllType) {
      this.selectedTrieKey = subscribedCurriculumInfo.trieKey
      this.params.schoolType = subscribedCurriculumInfo.schoolType
      this.params.grade = subscribedCurriculumInfo.gradeSubject
      this.params.semester = subscribedCurriculumInfo.semester
      this.params.revision = subscribedCurriculumInfo.revision
    }

    makeObservable(this, {
      elementaryDropdownList: observable,
      middleDropdownList: observable,
      highDropdownList: observable,
      selectedTrieKey: observable,
    })
  }

  override set(
    data: Omit<Partial<ParamT>, 'selectedTrieKey'> & {
      trieKey?: SchoolGradeSemesterSubjectFilter<ParamT>['selectedTrieKey']
    },
  ) {
    const _data = { ...data }
    delete _data.trieKey

    if (data.trieKey) {
      this.selectedTrieKey = data.trieKey
      if (data.trieKey === 'ALL' || !data.trieKey) {
        super.set({
          ..._data,
          grade: null,
          semester: null,
          revision: null,
        })
      } else {
        const item = this.selectedTrieKey.includes(
          CurriculumEntity.UnionGradeCurriculumCoreItem.UNION_KEY,
        )
          ? curriculumCoreService.getUnionItemByUnionTrieKey(this.selectedTrieKey)
          : curriculumCoreService.getItemByTrieKey(this.selectedTrieKey)
        if (item) {
          super.set({
            ..._data,
            schoolType: item.schoolType || 'ALL',
            semester: item.semester || 'ALL',
            grade: item.gradeSubject || 'ALL',
            revision: item.revision || 'ALL',
          } as Partial<ParamT>)
        }
      }
      return
    }
    super.set(_data as Partial<ParamT>)
  }
}
